import { action, comparer, computed, runInAction, toJS, when } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type { IAddOptions, TConstraints, TRachisEmpty } from '@feathr/rachis';
import { Collection, isWretchError, wretch } from '@feathr/rachis';

import { BlackbaudRaisersEdgeContactMappingStore } from './blackbaud_raisers_edge_contact_mappings';
import { FieldDataType } from './custom_fields';
import type { EIntegrationSyncRule, IIntegrationAttributes } from './integrations';
import { BaseIntegration } from './integrations';
import type {
  IIntegrationBaseMapping,
  TIntegrationDataType,
  TIntegrationPersonAttributes,
} from './integrations/integrations';
import Session from './Session';

// The Blackbaud user that is authenticated with the Blackbaud Raiser's Edge NXT product.
export interface IBlackbaudAuthenticatedUser {
  email: string;
  family_name: string;
  given_name: string;
  user_id: string;
}

export interface IDownsyncActivities {
  constituents: boolean;
  gift_events: boolean;
}

export interface IUpsyncActivities {
  donations: boolean;
  /** Whether Feathr creates new constituents from Feathr people. Gates every person upsync source. */
  people?: boolean;
  /** Whether a Feathr form submission creates a new constituent. Applies only while `people` is on. */
  people_from_form_submissions?: boolean;
}

export interface IFundraising {
  fund_id?: string;
}

export enum EBlackbaudRaisersEdgeUnsubscribeSyncActions {
  RequestsNoEmail = 'requests_no_email',
  SolicitCode = 'solicit_code',
  DoNotSync = 'do_not_sync',
}

export interface IBlackbaudUnsubscribeSyncPreference {
  action: EBlackbaudRaisersEdgeUnsubscribeSyncActions;
  solicit_code?: string;
}

// Integration for the Blackbaud Raiser's Edge NXT product.
export interface IBlackbaudRaisersEdgeIntegrationAttributes extends IIntegrationAttributes {
  authenticated_user?: IBlackbaudAuthenticatedUser;
  downsync_activities: IDownsyncActivities;
  fundraising?: IFundraising;
  upsync_activities: IUpsyncActivities;
  login_url: string;
  type: 'BlackbaudRaisersEdgeIntegration';
  unsubscribe_sync_preference: IBlackbaudUnsubscribeSyncPreference;
  /** Whether or not the integration has gift subscriptions in a `provisioning` state. */
  has_provisioning_gift_subscriptions: boolean;
  /** Read-only. Whether this connection can upsync people, i.e. magpie owns its sync. */
  person_upsync_available?: boolean;
}

export enum EBREContactMappings {
  // Strings
  age = 'age',
  email = 'email',
  first = 'first',
  former_name = 'former_name',
  fundraiser_status = 'fundraiser_status',
  gender = 'gender',
  id = 'id',
  last = 'last',
  lookup_id = 'lookup_id',
  marital_status = 'marital_status',
  middle = 'middle',
  name = 'name',
  phone = 'phone',
  preferred_name = 'preferred_name',
  primary_addressee = 'primary_addressee',
  primary_salutation = 'primary_salutation',
  suffix = 'suffix',
  suffix_2 = 'suffix_2',
  title = 'title',
  title_2 = 'title_2',
  type = 'type',

  // Dates
  birthdate = 'birthdate',
  date_added = 'date_added',
  date_modified = 'date_modified',
  date_of_first_gift = 'date_of_first_gift',
  date_of_last_gift = 'date_of_last_gift',
  deceased_date = 'deceased_date',

  // Booleans
  address = 'address',
  deceased = 'deceased',
  gives_anonymously = 'gives_anonymously',
  inactive = 'inactive',

  // Numbers
  consecutive_years_given = 'consecutive_years_given',
  first_gift_amount = 'first_gift_amount',
  greatest_gift_amount = 'greatest_gift_amount',
  last_gift_amount = 'last_gift_amount',
  total_giving = 'total_giving',
  total_years_given = 'total_years_given',

  // Lists
  constituent_codes = 'constituent_codes',
  emails = 'emails',
  solicit_codes = 'solicit_codes',
}

export type TBlackbaudContactMappingKey = keyof typeof EBREContactMappings;

/** The data type each Raiser's Edge contact field syncs as. Mirrors the server's mapping types. */
export const RAISERS_EDGE_CONTACT_DATA_TYPES: Readonly<
  Record<EBREContactMappings, TIntegrationDataType>
> = {
  [EBREContactMappings.address]: 'str',
  [EBREContactMappings.age]: 'int',
  [EBREContactMappings.birthdate]: 'date',
  [EBREContactMappings.consecutive_years_given]: 'int',
  [EBREContactMappings.constituent_codes]: 'list',
  [EBREContactMappings.date_added]: 'date',
  [EBREContactMappings.date_modified]: 'date',
  [EBREContactMappings.date_of_first_gift]: 'date',
  [EBREContactMappings.date_of_last_gift]: 'date',
  [EBREContactMappings.deceased]: 'bool',
  [EBREContactMappings.deceased_date]: 'date',
  [EBREContactMappings.email]: 'str',
  [EBREContactMappings.emails]: 'list',
  [EBREContactMappings.first]: 'str',
  [EBREContactMappings.first_gift_amount]: 'float',
  [EBREContactMappings.former_name]: 'str',
  [EBREContactMappings.fundraiser_status]: 'str',
  [EBREContactMappings.gender]: 'str',
  [EBREContactMappings.gives_anonymously]: 'bool',
  [EBREContactMappings.greatest_gift_amount]: 'float',
  [EBREContactMappings.id]: 'str',
  [EBREContactMappings.inactive]: 'bool',
  [EBREContactMappings.last]: 'str',
  [EBREContactMappings.last_gift_amount]: 'float',
  [EBREContactMappings.lookup_id]: 'str',
  [EBREContactMappings.marital_status]: 'str',
  [EBREContactMappings.middle]: 'str',
  [EBREContactMappings.name]: 'str',
  [EBREContactMappings.phone]: 'str',
  [EBREContactMappings.preferred_name]: 'str',
  [EBREContactMappings.primary_addressee]: 'str',
  [EBREContactMappings.primary_salutation]: 'str',
  [EBREContactMappings.solicit_codes]: 'list',
  [EBREContactMappings.suffix]: 'str',
  [EBREContactMappings.suffix_2]: 'str',
  [EBREContactMappings.title]: 'str',
  [EBREContactMappings.title_2]: 'str',
  [EBREContactMappings.total_giving]: 'float',
  [EBREContactMappings.total_years_given]: 'int',
  [EBREContactMappings.type]: 'str',
};
export interface IBlackbaudContactMapping extends IIntegrationBaseMapping {
  key: TBlackbaudContactMappingKey;
  integration: IBlackbaudRaisersEdgeIntegrationAttributes['id'];
  /**
   * The Feathr person attribute the Blackbaud field syncs to, unless `custom_field` is set. The
   * server also sets `address`, `emails` and `phone` on the mappings it creates.
   */
  default_field: TIntegrationPersonAttributes | 'address' | 'emails' | 'phone' | null;
  type: 'BlackbaudRaisersEdgeContactMapping';
  /** Whether or not a contact mapping is in a `provisioning` state. Used to prevent a user from deleting a subscription and cause a failure. */
  has_provisioning_subscriptions: boolean;
  /** Legacy mappings are always `syncing`. Optional until anhinga#9485 deploys. */
  status?: TBlackbaudContactMappingStatus;
  /** Why the mapping stopped, e.g. `deleted`. `null` on a stopped mapping means the customer turned it off. */
  stopped_reason?: string | null;
}

export type TBlackbaudContactMappingStatus = 'syncing' | 'stopped';

export type TRaisersEdgeSyncSummaryField =
  | 'last_synced'
  | 'constituents_synced'
  | 'mappings_need_attention';

/** The summary bar values. A value that failed to load is `null` and named in `errors`. */
export interface IRaisersEdgeSyncSummary extends Record<string, unknown> {
  /** ISO 8601 time of the latest downsync activity, `null` if it never synced. */
  last_synced: string | null;
  /** People with a non-email `external_id`. iMIS and CSV imports also write it, so it can count high. */
  constituents_synced: number | null;
  /** Mappings stopped for a reason the customer must fix. */
  mappings_need_attention: number | null;
  errors: TRaisersEdgeSyncSummaryField[];
}

/**
 * A change to one contact mapping. `customField` and `defaultField` are both required because the
 * request always sends both: a mapping targets one or the other, so set one and clear the other
 * with `null`.
 */
export interface IBlackbaudContactMappingUpdate {
  customField: string | null;
  defaultField: TIntegrationPersonAttributes | null;
  /** The server value, e.g. `down_sync`. */
  syncRule?: EIntegrationSyncRule;
  mapping: IBlackbaudContactMapping['id'];
}

/**
 * The attributes the v2 page stages on the model for Save & Sync. A dirty attribute that is not in
 * this list is one the server set outside a save. The page never sends it.
 */
export const RAISERS_EDGE_STAGED_ATTRIBUTES = [
  'downsync_activities',
  'fundraising',
  'unsubscribe_sync_preference',
  'upsync_activities',
] as const satisfies ReadonlyArray<keyof IBlackbaudRaisersEdgeIntegrationAttributes>;

export type TRaisersEdgeStagedAttribute = (typeof RAISERS_EDGE_STAGED_ATTRIBUTES)[number];

export interface ISolicitCodes extends Record<string, unknown> {
  solicit_codes: string[];
}

export interface IBlackbaudFund {
  id: string;
  description: string;
}

export interface IBlackbaudFunds extends Record<string, unknown> {
  funds: IBlackbaudFund[];
}

export enum EBREActivityMappings {
  gift_events = 'gift_events',
}

export enum EBlackbaudRaisersEdgeActivityDataTypes {
  gift_events = FieldDataType.str,
}

export class BlackbaudRaisersEdgeIntegration extends BaseIntegration<IBlackbaudRaisersEdgeIntegrationAttributes> {
  public readonly className = 'BlackbaudRaisersEdgeIntegration';

  public get constraints(): TConstraints<IBlackbaudRaisersEdgeIntegrationAttributes> {
    return {
      'unsubscribe_sync_preference.action': {
        presence: {
          allowEmpty: false,
          message: '^Unsubscribe action must not be blank.',
        },
      },
      'unsubscribe_sync_preference.solicit_code': {
        presenceUnless: {
          allowEmpty: false,
          message: '^Unsubscribe solicit code must not be blank.',
          unless: (attributes) => attributes.unsubscribe_sync_preference.action !== 'solicit_code',
        },
      },
    };
  }

  public getItemUrl(pathSuffix: string | undefined): string {
    return concatPath(`integrations/raisers-edge/${this.id}`, pathSuffix);
  }

  public get name(): string {
    return 'raisers_edge_integration';
  }

  private contactMappingStoreInstance?: BlackbaudRaisersEdgeContactMappingStore;

  /** Observable contact mappings, shared by every reader of this integration. */
  public get contactMappingStore(): BlackbaudRaisersEdgeContactMappingStore {
    this.contactMappingStoreInstance ??= new BlackbaudRaisersEdgeContactMappingStore(this);
    return this.contactMappingStoreInstance;
  }

  /**
   * The staged attributes whose value differs from the saved one. An edit set back to the saved
   * value doesn't count, and neither does an attribute the server dirtied.
   */
  @computed
  public get changedStagedAttributes(): TRaisersEdgeStagedAttribute[] {
    return RAISERS_EDGE_STAGED_ATTRIBUTES.filter(
      (attribute) =>
        this.isAttributeDirty(attribute) &&
        !comparer.structural(toJS(this.get(attribute)), toJS(this.shadowAttributes[attribute])),
    );
  }

  /**
   * The changed staged attributes as one PATCH body. Each nested object is sent whole, because the
   * server replaces it rather than merging it.
   */
  public getStagedPatch(): Partial<IBlackbaudRaisersEdgeIntegrationAttributes> {
    return Object.fromEntries(
      this.changedStagedAttributes.map((attribute) => [attribute, toJS(this.get(attribute))]),
    );
  }

  /** Puts every dirty staged attribute back to its saved value and marks it clean. */
  @action
  public discardStagedChanges(): void {
    RAISERS_EDGE_STAGED_ATTRIBUTES.filter((attribute) => this.isAttributeDirty(attribute)).forEach(
      (attribute) => {
        this.set({ [attribute]: this.shadowAttributes[attribute] }, false);
        this.setAttributeClean(attribute);
      },
    );
  }

  // This url will not be necessary once we create the BlackbaudContactMappings collection class.
  public url(): string {
    this.assertCollection(this.collection);

    return `${this.collection.url()}${this.id}/contact_mappings`;
  }

  /*
   * Blackbaud Contact Mapping CRUD methods
   * These are a temporary measure. They will not be necessary once the work is done to
   * create an BlackbaudContactMappings collection class in #2651 and #2722.
   */

  // GET
  @action
  public async getContactMappings(): Promise<IBlackbaudContactMapping[]> {
    this.assertCollection(this.collection);

    // When called from Perch (which views arbitrary accounts), the collection must have
    // `accountIdScope` set (via `createAccountScopedCollection` / `useAccountScopedCollection`).
    // The app-level `getHeaders` override in `extender/src/index.tsx` reads `accountIdScope` and
    // forwards it to `Session.getHeaders`, scoping this request to the viewed account.
    // TODO: Implement sorting in the query as part of #2991.
    const response = await wretch<IBlackbaudContactMapping[]>(this.url(), {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  private async addContactMapping(
    key: TBlackbaudContactMappingKey,
  ): Promise<IBlackbaudContactMapping> {
    this.assertCollection(this.collection);

    const response = await wretch<IBlackbaudContactMapping>(this.url(), {
      headers: this.collection.getHeaders(),
      method: 'POST',
      body: JSON.stringify({ contact_mapping: key }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getSolicitCodes(): Promise<ISolicitCodes> {
    this.assertCollection(this.collection);

    const url = `${this.collection.url()}${this.id}/solicit_codes`;
    const response = await wretch<ISolicitCodes>(url, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getSyncSummary(): Promise<IRaisersEdgeSyncSummary> {
    this.assertCollection(this.collection);

    const url = `${this.collection.url()}${this.id}/sync_summary`;
    const response = await wretch<IRaisersEdgeSyncSummary>(url, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public async getFunds(): Promise<IBlackbaudFunds> {
    this.assertCollection(this.collection);

    const url = `${this.collection.url()}${this.id}/funds`;
    const response = await wretch<IBlackbaudFunds>(url, {
      headers: this.collection.getHeaders(),
      method: 'GET',
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  // POST
  @action
  public async addContactMappings(
    mappings: TBlackbaudContactMappingKey[],
  ): Promise<IBlackbaudContactMapping[]> {
    const response = Promise.all(mappings.map((mapping) => this.addContactMapping(mapping)));
    return response;
  }

  // PATCH
  @action
  public async updateContactMapping({
    mapping,
    customField,
    defaultField,
    syncRule,
  }: IBlackbaudContactMappingUpdate): Promise<IBlackbaudContactMapping | null> {
    this.assertCollection(this.collection);

    const url = `${this.url()}/${mapping}`;
    // A magpie-owned integration deletes a mapping updated to no Feathr field, and answers 204.
    const response = await wretch<IBlackbaudContactMapping | null>(url, {
      method: 'PATCH',
      headers: this.collection.getHeaders(),
      body: JSON.stringify({
        // When patching a field you can either set the custom_field or the default_field, but not both.
        custom_field: customField,
        default_field: defaultField,
        sync_rule: syncRule,
      }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  // DELETE
  @action
  public async deleteContactMapping(id: string): Promise<void> {
    this.assertCollection(this.collection);

    const url = `${this.url()}/${id}`;
    const response = await wretch<TRachisEmpty>(url, {
      headers: this.collection.getHeaders(),
      method: 'DELETE',
    });

    if (isWretchError(response)) {
      throw new Error('Failed to delete contact mapping.');
    }
  }

  /**
   * Complete OAuth authentication by calling POST to /authenticate endpoint.
   * This should be called after the user returns from Blackbaud OAuth.
   */
  @action
  public async authenticate(authCode: string, redirectUri: string): Promise<void> {
    this.assertCollection(this.collection);

    const url = `${this.collection.url()}${this.id}/authenticate`;
    const response = await wretch<IBlackbaudRaisersEdgeIntegrationAttributes>(url, {
      headers: this.collection.getHeaders(),
      method: 'POST',
      body: JSON.stringify({ auth_code: authCode, redirect_uri: redirectUri }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    // The server saved these values, so the page must not offer them as unsaved changes.
    this.applyServerAttributes(response.data);
  }
}

export class BlackbaudRaisersEdgeIntegrations extends Collection<BlackbaudRaisersEdgeIntegration> {
  public getModel(
    attributes: IBlackbaudRaisersEdgeIntegrationAttributes,
  ): BlackbaudRaisersEdgeIntegration {
    return new BlackbaudRaisersEdgeIntegration(attributes);
  }

  public getClassName(): string {
    return 'raisers_edge_integration';
  }

  public override url(): string {
    return `${this.getHostname()}integrations/raisers-edge/`;
  }

  public override async add(
    model: BlackbaudRaisersEdgeIntegration,
    options: Partial<IAddOptions> & { redirectUri?: string } = {},
  ): Promise<BlackbaudRaisersEdgeIntegration> {
    const { validate = true, refreshApiCache = true, redirectUri } = options;
    if (validate && !model.isValid()) {
      throw new Error('Model is invalid');
    }
    const url = `${BLACKBOX_URL}integrations/raisers-edge/`;

    const response = await wretch<IBlackbaudRaisersEdgeIntegrationAttributes>(url, {
      headers: this.getHeaders(),
      method: 'POST',
      body: redirectUri ? JSON.stringify({ redirect_uri: redirectUri }) : undefined,
    });

    if (isWretchError(response)) {
      this.processErrorResponse(model, response.error);
      return model;
    }

    if (refreshApiCache) {
      this.refreshApiCache();
    }

    return this.processJSONResponse(response);
  }

  /**
   * Returns the account's integration, and creates it first when there is none (an account has at
   * most one). Rejects when the list fails, so a failed load never creates a second record.
   */
  public async findOrCreate(redirectUri: string): Promise<BlackbaudRaisersEdgeIntegration> {
    let response = this.list();
    if (response.isErrored) {
      // A failed list stays cached, so retry it rather than trust its empty result.
      response = this.list({}, { reset: true });
    }
    await when(() => !response.isPending);
    if (response.isErrored) {
      throw response.error ?? new Error('The integration list did not load.');
    }
    if (response.models.length) {
      return response.models[0];
    }

    // Skip the list refresh: it empties the list while it reloads, which unmounts a page that renders it.
    const created = await this.add(this.create(), {
      redirectUri,
      refreshApiCache: false,
      validate: false,
    });
    if (created.isErrored) {
      throw created.error ?? new Error('The integration was not created.');
    }
    // Add the new record to the cached list, so a later call finds it instead of creating another.
    runInAction(() => response.models.push(created));
    return created;
  }

  /**
   * Exchange OAuth authorization code for access token.
   * POST /integrations/raisers-edge/authorize with { code: "..." }
   */
  public async authorize(code: string): Promise<BlackbaudRaisersEdgeIntegration> {
    const url = `${this.getHostname()}integrations/raisers-edge/authorize`;

    const response = await wretch<IBlackbaudRaisersEdgeIntegrationAttributes>(url, {
      headers: this.getHeaders(),
      method: 'POST',
      body: JSON.stringify({ auth_code: code }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    this.clearApiCache();
    return this.processJSONResponse(response);
  }
}

/** Whether legacy or magpie owns a Raiser's Edge integration's data flow during the cutover. */
export enum ERaisersEdgeSyncOwner {
  Legacy = 'legacy',
  Magpie = 'magpie',
}

/** One audited change of owner. */
export interface IRaisersEdgeSyncOwnerChange {
  actor: string;
  actor_email: string | null;
  date_created: string;
  from_owner: ERaisersEdgeSyncOwner;
  magpie_connection_id: string | null;
  to_owner: ERaisersEdgeSyncOwner;
}

/** A move to magpie that runs once domain-event publishing has reached every worker. */
export interface IRaisersEdgePendingMove {
  magpie_connection_id: string | null;
  requested_by: string | null;
  requested_by_email: string | null;
  run_at: string;
}

/** Why the last scheduled move to magpie was refused. */
export interface IRaisersEdgeMoveFailure {
  failed_at: string;
  reason: string;
}

/**
 * The integration's owner and its latest owner changes, newest first. `pending_move` is set while
 * a move to magpie waits for publishing to reach every worker; older servers omit both.
 */
export interface IRaisersEdgeSyncOwner extends Record<string, unknown> {
  history: IRaisersEdgeSyncOwnerChange[];
  last_move_failure?: IRaisersEdgeMoveFailure | null;
  magpie_connection_id: string | null;
  owner: ERaisersEdgeSyncOwner;
  pending_move?: IRaisersEdgePendingMove | null;
}

/** Body for changing the owner. A connection id is required to move to magpie. */
export interface IRaisersEdgeSyncOwnerUpdate {
  magpie_connection_id?: string;
  owner: ERaisersEdgeSyncOwner;
}

function syncOwnerUrl(): string {
  return `${BLACKBOX_URL}integrations/raisers-edge/sync-owner`;
}

/**
 * Reads whether legacy or magpie owns an account's Raiser's Edge data flow. Sudo only.
 *
 * @param accountId - The account whose integration to read.
 */
export async function fetchRaisersEdgeSyncOwner(accountId: string): Promise<IRaisersEdgeSyncOwner> {
  const response = await wretch<IRaisersEdgeSyncOwner>(syncOwnerUrl(), {
    method: 'GET',
    headers: Session.getHeaders(true, accountId),
  });
  if (isWretchError(response)) {
    throw response.error;
  }
  return response.data;
}

/**
 * Moves an account's Raiser's Edge data flow to another owner, auditing the change. Sudo only.
 *
 * @param accountId - The account whose integration to change.
 * @param update - The owner to move to, and the magpie connection when moving to magpie.
 */
export async function updateRaisersEdgeSyncOwner(
  accountId: string,
  update: IRaisersEdgeSyncOwnerUpdate,
): Promise<IRaisersEdgeSyncOwner> {
  const response = await wretch<IRaisersEdgeSyncOwner>(syncOwnerUrl(), {
    method: 'PUT',
    body: JSON.stringify(update),
    headers: Session.getHeaders(true, accountId),
  });
  if (isWretchError(response)) {
    throw response.error;
  }
  return response.data;
}

/** A legacy Raiser's Edge sync direction an operator can stop during the cutover. */
export enum ERaisersEdgeLegacySyncDirection {
  Downsync = 'downsync',
  Upsync = 'upsync',
}

/** One audited flip of a legacy kill switch. */
export interface IRaisersEdgeKillSwitchFlip {
  actor: string;
  actor_email: string | null;
  date_created: string;
  direction: ERaisersEdgeLegacySyncDirection;
  enabled: boolean;
}

/** Whether each legacy direction runs, and the latest switch flips, newest first. */
export interface IRaisersEdgeKillSwitches extends Record<string, unknown> {
  history: IRaisersEdgeKillSwitchFlip[];
  legacy_downsync_enabled: boolean;
  legacy_upsync_enabled: boolean;
}

function killSwitchesUrl(): string {
  return `${BLACKBOX_URL}integrations/raisers-edge/kill-switches`;
}

/**
 * Reads an account's legacy Raiser's Edge sync kill switches. Sudo only.
 *
 * @param accountId - The account whose integration to read.
 */
export async function fetchRaisersEdgeKillSwitches(
  accountId: string,
): Promise<IRaisersEdgeKillSwitches> {
  const response = await wretch<IRaisersEdgeKillSwitches>(killSwitchesUrl(), {
    method: 'GET',
    headers: Session.getHeaders(true, accountId),
  });
  if (isWretchError(response)) {
    throw response.error;
  }
  return response.data;
}

/**
 * Turns one legacy Raiser's Edge sync direction on or off, auditing the flip. Sudo only.
 *
 * @param accountId - The account whose integration to change.
 * @param direction - The direction to flip.
 * @param enabled - Whether the direction should run.
 */
export async function setRaisersEdgeKillSwitch(
  accountId: string,
  direction: ERaisersEdgeLegacySyncDirection,
  enabled: boolean,
): Promise<IRaisersEdgeKillSwitches> {
  const response = await wretch<IRaisersEdgeKillSwitches>(killSwitchesUrl(), {
    method: 'PUT',
    body: JSON.stringify({ direction, enabled }),
    headers: Session.getHeaders(true, accountId),
  });
  if (isWretchError(response)) {
    throw response.error;
  }
  return response.data;
}
